import React, { useEffect, useRef, useState } from "react";

import { CaretDown, StatusDot } from "features/player/icons";

// "Search by lesson title" dropdown (univ.mekari.com): lists every lesson by
// chapter, filters as you type, and opens the one you pick. Arrow keys move,
// Enter opens, Escape closes.
export default function LessonPicker({ lessons, current, done, onPick }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const box = useRef(null);
  const list = useRef(null);

  const q = query.trim().toLowerCase();
  const matches = lessons.filter((l) => !q || `${l.name} ${l.chapter.name}`.toLowerCase().includes(q));

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => box.current && !box.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    const option = list.current && list.current.querySelector(`[data-index="${active}"]`);
    if (option) option.scrollIntoView({ block: "nearest" });
  }, [active]);

  function pick(lesson) {
    setOpen(false);
    setQuery("");
    onPick(lesson);
  }

  function onKeyDown(e) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      const step = e.key === "ArrowDown" ? 1 : -1;
      setActive((i) => Math.min(Math.max(i + step, 0), Math.max(matches.length - 1, 0)));
    } else if (e.key === "Enter" && open && matches[active]) {
      e.preventDefault();
      pick(matches[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  let lastChapter = null;
  return (
    <div className="yc-player-picker" ref={box}>
      <label className="yc-player-search">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="lesson-picker-list"
          aria-autocomplete="list"
          aria-label="Search by lesson title"
          placeholder="Search by lesson title"
          value={query}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <button type="button" className="yc-player-picker-toggle" tabIndex={-1} aria-label="Show all lessons" onClick={() => setOpen((o) => !o)}>
          <CaretDown />
        </button>
      </label>
      {open && (
        <ul className="yc-player-picker-list" id="lesson-picker-list" role="listbox" ref={list}>
          {matches.length === 0 && <li className="yc-player-picker-empty">No lessons match "{query}".</li>}
          {matches.map((lesson, i) => {
            const heading = lesson.chapter.id !== lastChapter ? lesson.chapter.name : null;
            lastChapter = lesson.chapter.id;
            return (
              <React.Fragment key={lesson.id}>
                {heading && <li className="yc-player-picker-chapter" role="presentation">{heading}</li>}
                <li
                  role="option"
                  data-index={i}
                  aria-selected={i === active}
                  className={`yc-player-picker-option${i === active ? " is-active" : ""}${lesson.id === current.id ? " is-current" : ""}`}
                  onMouseEnter={() => setActive(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(lesson);
                  }}
                >
                  <StatusDot done={done.has(lesson.id)} size={16} />
                  <span>{lesson.name}</span>
                  {lesson.id === current.id && <small>Now playing</small>}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
